

import { useState } from 'react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Paper,
  Badge,
  ActionIcon,
  CopyButton,
  Tooltip,
  Alert,
  Select,
  Divider,
  Box,
} from '@mantine/core';
import {
  IconShare,
  IconCopy,
  IconCheck,
  IconTrash,
  IconAlertCircle,
  IconLink,
  IconEye,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shareLinksApi, AutoPublished } from '@/services/share-links.api';
import { settingsApi } from '@/services/settings.api';
import { notifications } from '@mantine/notifications';

interface Props {
  opened: boolean;
  onClose: () => void;
  flowId: string;
  versions: any[];
}

export default function ShareLinksModal({ opened, onClose, flowId, versions }: Props) {
  const [selectedVersionId, setSelectedVersionId] = useState<string | undefined>(undefined);
  const [lastAutoPublished, setLastAutoPublished] = useState<AutoPublished>(null);
  const queryClient = useQueryClient();

  const { data: shareLinks = [], isLoading } = useQuery({
    queryKey: ['shareLinks', flowId],
    queryFn: () => shareLinksApi.getShareLinks(flowId),
    enabled: opened,
  });

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: opened,
  });

  const createLinkMutation = useMutation({
    mutationFn: () => shareLinksApi.createShareLink(flowId, selectedVersionId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['shareLinks', flowId] });
      // Reflect the newly published version in the editor's version menu / badge.
      if (result.autoPublished) {
        queryClient.invalidateQueries({ queryKey: ['flow', flowId] });
        setLastAutoPublished(result.autoPublished);
      }
      setSelectedVersionId(undefined);
      notifications.show({
        title: 'Share link created',
        message: 'The share link has been created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Share link not created',
        // The API's message names the actual problem (for example, several
        // active links needing to be revoked first); axios only reports the
        // status code.
        message:
          error?.response?.data?.message || error?.message || 'Failed to create share link',
        color: 'red',
      });
    },
  });

  const revokeLinkMutation = useMutation({
    mutationFn: (token: string) => shareLinksApi.revokeShareLink(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shareLinks', flowId] });
      notifications.show({
        title: 'Link revoked',
        message: 'The share link has been revoked',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to revoke share link',
        color: 'red',
      });
    },
  });

  const getShareUrl = (token: string) => {
    if (typeof window !== 'undefined') {
      // Short form: the client's telephony system cannot send URLs of 56+
      // characters. https://portal.hollowayhq.io/s/<12-char token> is 43.
      return `${window.location.origin}/s/${token}`;
    }
    return '';
  };

  const activeLinks = shareLinks.filter((link: any) => !link.revokedAt);
  const revokedLinks = shareLinks.filter((link: any) => link.revokedAt);

  // Default to the stricter behaviour while settings load, so the create
  // controls cannot flash on and then vanish.
  const singleLinkMode = !(settings?.allowMultipleShareLinks ?? false);
  // In single-link mode a flow that already has a link has nothing to create —
  // the existing link is listed below with copy and revoke.
  const canCreate = !singleLinkMode || activeLinks.length === 0;

  const versionOptions = [
    { value: '', label: 'Latest published version' },
    ...versions.map((v: any) => ({
      value: v.id,
      label: `Version ${v.version} (${v.status})`,
    })),
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconShare size={20} />
          <Text fw={600}>Share Links</Text>
        </Group>
      }
      size="lg"
    >
      <Stack gap="md">
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          Share links allow anyone with the link to view your flow in read-only mode.
          {singleLinkMode &&
            ' Each flow has one link, which always shows the latest published version. Revoke it to issue a new one.'}
        </Alert>

        {lastAutoPublished && (
          <Alert icon={<IconCheck size={16} />} color="teal" withCloseButton onClose={() => setLastAutoPublished(null)}>
            Published v{lastAutoPublished.version} so this link has something to show.
          </Alert>
        )}

        {/* Create section. Hidden once a flow has its link in single-link mode:
            there is nothing to configure, and the link itself is shown below. */}
        {canCreate && (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Text fw={500} size="sm">
                {singleLinkMode ? 'Create Share Link' : 'Create New Share Link'}
              </Text>
              {/* The version picker only applies when a flow can hold several
                  links. The single link is deliberately unpinned so it follows
                  the latest published version. */}
              {!singleLinkMode && (
                <Select
                  label="Version to share"
                  placeholder="Select version or use latest"
                  data={versionOptions}
                  value={selectedVersionId || ''}
                  onChange={(value) => setSelectedVersionId(value || undefined)}
                  description="Choose a specific version or leave empty to always share the latest published version"
                />
              )}
              <Button
                leftSection={<IconLink size={16} />}
                onClick={() => createLinkMutation.mutate()}
                loading={createLinkMutation.isPending}
                fullWidth
              >
                Create Share Link
              </Button>
            </Stack>
          </Paper>
        )}

        <Divider label="Active Share Links" labelPosition="center" />

        {/* Active Links List */}
        {isLoading ? (
          <Text size="sm" c="dimmed">
            Loading share links...
          </Text>
        ) : activeLinks.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No active share links yet
          </Text>
        ) : (
          <Stack gap="sm">
            {activeLinks.map((link: any) => (
              <Paper key={link.id} p="md" withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <IconEye size={16} />
                      <Text size="sm" fw={500}>
                        {link.versionId
                          ? `Version ${versions.find((v) => v.id === link.versionId)?.version || '?'}`
                          : 'Latest published'}
                      </Text>
                    </Group>
                    <Group gap="xs">
                      <Badge size="sm" variant="light">
                        {link.accessCount} {link.accessCount === 1 ? 'view' : 'views'}
                      </Badge>
                      <Tooltip label="Revoke access">
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => revokeLinkMutation.mutate(link.token)}
                          loading={revokeLinkMutation.isPending}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>

                  <Group gap="xs">
                    <Box
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getShareUrl(link.token)}
                    </Box>
                    <CopyButton value={getShareUrl(link.token)} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied!' : 'Copy link'}>
                          <ActionIcon
                            color={copied ? 'teal' : 'blue'}
                            variant="light"
                            onClick={async () => {
                              try {
                                // Try modern clipboard API first
                                if (navigator.clipboard && window.isSecureContext) {
                                  await navigator.clipboard.writeText(getShareUrl(link.token));
                                  copy(); // Trigger the UI state change
                                } else {
                                  // Fallback for older browsers or non-HTTPS
                                  const textArea = document.createElement('textarea');
                                  textArea.value = getShareUrl(link.token);
                                  textArea.style.position = 'fixed';
                                  textArea.style.left = '-999999px';
                                  document.body.appendChild(textArea);
                                  textArea.focus();
                                  textArea.select();
                                  try {
                                    document.execCommand('copy');
                                    copy(); // Trigger the UI state change
                                  } catch (err) {
                                    console.error('Fallback copy failed:', err);
                                  }
                                  document.body.removeChild(textArea);
                                }
                              } catch (err) {
                                console.error('Failed to copy to clipboard:', err);
                                notifications.show({
                                  title: 'Copy failed',
                                  message: 'Could not copy to clipboard. Please copy manually.',
                                  color: 'red',
                                });
                              }
                            }}
                          >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>

                  <Text size="xs" c="dimmed">
                    Created {new Date(link.createdAt).toLocaleDateString()}
                    {link.lastAccessedAt &&
                      ` • Last accessed ${new Date(link.lastAccessedAt).toLocaleDateString()}`}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Revoked Links Section */}
        {revokedLinks.length > 0 && (
          <>
            <Divider label="Revoked Links" labelPosition="center" />
            <Stack gap="sm">
              {revokedLinks.map((link: any) => (
                <Paper key={link.id} p="md" withBorder opacity={0.6}>
                  <Group justify="space-between">
                    <div>
                      <Text size="sm" fw={500} td="line-through">
                        {link.versionId
                          ? `Version ${versions.find((v) => v.id === link.versionId)?.version || '?'}`
                          : 'Latest published'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Revoked {new Date(link.revokedAt).toLocaleDateString()} • {link.accessCount}{' '}
                        total views
                      </Text>
                    </div>
                    <Badge size="sm" color="red" variant="light">
                      Revoked
                    </Badge>
                  </Group>
                </Paper>
              ))}
            </Stack>
            <Text size="xs" c="dimmed" ta="center">
              Revoked links will be automatically deleted after 30 days
            </Text>
          </>
        )}
      </Stack>
    </Modal>
  );
}
